import React, { ReactNode, useMemo } from 'react';
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Tooltip,
  ComposedChart,
  Line,
  Area,
  CartesianGrid,
  Customized,
} from 'recharts';
import { Widget } from './Widget';
import { useWidgetZoomControlsPublisher } from './WidgetZoomControlsContext';
import { DataResult, WidgetSettingsDto } from '@thingslog/repositories';
import { DataResultParser } from './parsing/DataResultParser';
import { DataResultParsed, DataResultParseStream } from './parsing/DataResultParsed';
import {
  ChartEmptyState,
  ChartDataDensityOverlay,
  ChartSeriesConfig,
  ChartTooltipContent,
  ChartTooltipPayload,
  ChartWidgetFrame,
  DEFAULT_CHART_COLORS,
  chartAxisTick,
  chartGridProps,
  getChartLegendProps,
  chartMargin,
  CHART_RESIZE_DEBOUNCE_MS,
  chartTooltipWrapperStyle,
  chartYAxisWidth,
  createChartSeries,
  createSmartDateFormatter,
  decimateChartData,
  getVisibleChartData,
  LINE_AREA_MAX_RENDER_POINTS,
  LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD,
  getChartSeriesLegendKeys,
  useChartLegendVisibility,
  useChartViewportControls,
} from './utils';
import { WidgetSettingsFactory } from '../../factories/WidgetSettings.factory';

const AREA_SERIES_PREFIX = '__line_area_fill__';
const MAX_LINE_CHART_POINT_DOTS = 100;
const ZOOMED_AUTO_POINT_DOTS_MAX_STREAM_POINTS = 50;

interface LineChartContentProps {
  data: { [key: string]: string | number }[];
  series: ChartSeriesConfig[];
  parsedData: DataResultParsed;
  widgetSettings: WidgetSettingsDto;
}

const createGradientId = (
  widgetSettings: WidgetSettingsDto,
  streamIdx: number,
  vizIdx: number
): string => {
  const normalizedTitle = String(widgetSettings.title ?? 'widget')
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase();
  return `line-chart-area-gradient-${widgetSettings.dataSourceId}-${normalizedTitle}-${streamIdx}-${vizIdx}`;
};

const filterLineTooltipPayload = (payload: ChartTooltipPayload[]): ChartTooltipPayload[] => {
  return payload
    .filter((item: ChartTooltipPayload) => {
      return !item.name?.startsWith(AREA_SERIES_PREFIX);
    })
    .filter(
      (
        item: ChartTooltipPayload,
        index: number,
        filteredPayload: ChartTooltipPayload[]
      ): boolean => {
        const firstMatchingIndex = filteredPayload.findIndex(
          (candidate: ChartTooltipPayload): boolean => {
            return candidate.dataKey === item.dataKey;
          }
        );

        return firstMatchingIndex === index;
      }
    );
};

const getLineStrokeWidth = (widgetSettings: WidgetSettingsDto): number => {
  if (widgetSettings.type !== 'line-chart') {
    return 3;
  }

  switch (widgetSettings.visualization.lineThickness) {
    case 'fine':
      return 2;
    case 'bold':
      return 4;
    case 'standard':
    default:
      return 3;
  }
};

const getLineInterpolation = (
  widgetSettings: WidgetSettingsDto
): 'linear' | 'monotone' | 'stepAfter' => {
  if (widgetSettings.type !== 'line-chart') {
    return 'monotone';
  }

  switch (widgetSettings.visualization.interpolation) {
    case 'straight':
      return 'linear';
    case 'stepped':
      return 'stepAfter';
    case 'smooth':
    default:
      return 'monotone';
  }
};

const LineChartContent = ({
  data,
  series,
  parsedData,
  widgetSettings,
}: LineChartContentProps): JSX.Element => {
  const visualization = widgetSettings.type === 'line-chart' ? widgetSettings.visualization : null;
  const hasViewportControls =
    data.length > LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD && !!parsedData.xAxis?.dataKey;
  const {
    canZoomIn,
    canZoomOut,
    containerClassName,
    containerProps,
    containerStyle,
    isZoomed,
    resetZoom,
    viewport,
    zoomIn,
    zoomOut,
  } = useChartViewportControls({ dataLength: data.length, enabled: hasViewportControls });

  useWidgetZoomControlsPublisher(
    hasViewportControls ? { canZoomIn, canZoomOut, isZoomed, resetZoom, zoomIn, zoomOut } : null
  );

  const { isSeriesHidden, toggleSeries } = useChartLegendVisibility(
    getChartSeriesLegendKeys(series)
  );

  const visibleData = useMemo(
    () => getVisibleChartData(data, viewport, hasViewportControls),
    [data, hasViewportControls, viewport]
  );
  const renderData = useMemo(
    () => decimateChartData(visibleData, LINE_AREA_MAX_RENDER_POINTS, parsedData.xAxis?.dataKey),
    [parsedData.xAxis?.dataKey, visibleData]
  );
  // Zoomed in far enough that every visible stream shows only a handful of
  // points: draw the dots even when the "show points" setting is off, so the
  // actual measurements are visible.
  const autoShowZoomedPoints = useMemo((): boolean => {
    const shownSeries = series.filter(
      (item: ChartSeriesConfig): boolean => !isSeriesHidden(item.dataKey)
    );

    if (!isZoomed || shownSeries.length === 0) {
      return false;
    }

    return shownSeries.every((item: ChartSeriesConfig): boolean => {
      let visiblePointCount = 0;

      for (const dataPoint of visibleData) {
        const value = dataPoint[item.dataKey];

        if (value !== undefined && value !== null) {
          visiblePointCount += 1;

          if (visiblePointCount >= ZOOMED_AUTO_POINT_DOTS_MAX_STREAM_POINTS) {
            return false;
          }
        }
      }

      return true;
    });
  }, [isSeriesHidden, isZoomed, series, visibleData]);
  const showPoints =
    (visualization?.showPoints === true && visibleData.length <= MAX_LINE_CHART_POINT_DOTS) ||
    autoShowZoomedPoints;
  const xAxisFormatter = useMemo(
    () =>
      createSmartDateFormatter(renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter),
    [renderData, parsedData.xAxis?.dataKey, parsedData.xAxis?.formatter]
  );

  return (
    <div
      className={`relative h-full w-full ${containerClassName}`}
      style={containerStyle}
      {...containerProps}
    >
      <ResponsiveContainer width="100%" height="100%" debounce={CHART_RESIZE_DEBOUNCE_MS}>
        <ComposedChart data={renderData} margin={chartMargin}>
          <defs>
            {series.map((item: ChartSeriesConfig) => (
              <linearGradient
                key={item.gradientId}
                id={item.gradientId ?? item.dataKey}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={item.color} stopOpacity={0.18} />
                <stop offset="55%" stopColor={item.color} stopOpacity={0.07} />
                <stop offset="100%" stopColor={item.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          {visualization?.showGrid !== false && <CartesianGrid {...chartGridProps} />}
          {parsedData.xAxis &&
            parsedData.xAxis.show &&
            visualization?.showXAxisLabels !== false && (
              <XAxis
                dataKey={parsedData.xAxis.dataKey}
                tickFormatter={xAxisFormatter}
                name={parsedData.xAxis.name}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                dy={8}
                minTickGap={24}
              />
            )}
          {parsedData.yAxis &&
            parsedData.yAxis.show &&
            visualization?.showYAxisLabels !== false && (
              <YAxis
                tickFormatter={parsedData.yAxis?.formatter}
                name={parsedData.yAxis?.name}
                axisLine={false}
                tickLine={false}
                tick={chartAxisTick}
                width={chartYAxisWidth}
              />
            )}
          {series.map((item: ChartSeriesConfig) => (
            <Area
              key={`area-${item.dataKey}`}
              dataKey={item.dataKey}
              type={getLineInterpolation(widgetSettings)}
              name={`${AREA_SERIES_PREFIX}${item.name}`}
              stroke="none"
              fill={`url(#${item.gradientId ?? item.dataKey})`}
              activeDot={false}
              dot={false}
              connectNulls={true}
              legendType="none"
              tooltipType="none"
              isAnimationActive={false}
              hide={isSeriesHidden(item.dataKey)}
            />
          ))}
          {series.map((item: ChartSeriesConfig) => (
            <Line
              key={`line-${item.dataKey}`}
              dataKey={item.dataKey}
              stroke={item.color}
              type={getLineInterpolation(widgetSettings)}
              name={item.name}
              strokeWidth={getLineStrokeWidth(widgetSettings)}
              dot={showPoints ? { r: 2.5, fill: item.color } : false}
              activeDot={
                showPoints
                  ? {
                      r: 5,
                      stroke: '#ffffff',
                      strokeWidth: 2,
                      fill: item.color,
                    }
                  : false
              }
              connectNulls={true}
              legendType="circle"
              isAnimationActive={false}
              hide={isSeriesHidden(item.dataKey)}
            />
          ))}
          <Tooltip
            wrapperStyle={chartTooltipWrapperStyle}
            content={
              <ChartTooltipContent
                fallbackColors={DEFAULT_CHART_COLORS}
                labelFormatter={(
                  value: string | number,
                  dataPoint?: { [key: string]: string | number }
                ): string =>
                  parsedData.tooltip?.labelFormatter?.(value, dataPoint) ?? String(value)
                }
                payloadFilter={filterLineTooltipPayload}
                valueFormatter={
                  parsedData.tooltip?.formatter
                    ? (value: number | string, name: string): [string, string] =>
                        parsedData.tooltip!.formatter!(value, name, parsedData.dataStreams)
                    : undefined
                }
              />
            }
          />
          {parsedData.legend && parsedData.legend.show && visualization?.showLegend !== false && (
            <Legend
              iconType="circle"
              payload={series.map((item: ChartSeriesConfig) => ({
                color: item.color,
                id: item.dataKey,
                inactive: isSeriesHidden(item.dataKey),
                type: 'circle' as const,
                value: item.name,
              }))}
              {...getChartLegendProps({
                aside: visualization?.renderLegendAside === true,
                onToggleSeries: series.length > 1 ? toggleSeries : undefined,
              })}
            />
          )}
          {hasViewportControls && (
            <Customized
              component={
                <ChartDataDensityOverlay
                  renderedPoints={renderData.length}
                  totalPoints={visibleData.length}
                />
              }
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export class LineChartWidget implements Widget {
  public hasScrollZoomControls(data: DataResult, widgetSettings: WidgetSettingsDto): boolean {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);

    return (
      dataParser.merge(parsedData).length > LINE_AREA_SCROLL_ZOOM_POINT_THRESHOLD &&
      !!parsedData.xAxis?.dataKey
    );
  }

  public render(data: DataResult, widgetSettings: WidgetSettingsDto): ReactNode {
    const normalizedSettings = WidgetSettingsFactory.normalize(widgetSettings);
    const dataParser = new DataResultParser(widgetSettings.query.type);
    const parsedData = dataParser.parse(data, normalizedSettings.type);
    const noData = parsedData.dataStreams.every(
      (stream: DataResultParseStream) => stream.data.length === 0
    );

    if (noData) {
      return (
        <ChartEmptyState
          descriptionKey="dashboard_widget_no_points"
          description="This widget has no points to plot."
        />
      );
    }

    const mergedData = dataParser.merge(parsedData);
    const series = createChartSeries(parsedData.dataStreams, {
      createGradientId: (streamIdx: number, vizIdx: number): string =>
        createGradientId(widgetSettings, streamIdx, vizIdx),
      getColor: ({ fill, stroke }: { fill?: string; stroke?: string }): string | undefined =>
        stroke ?? fill,
    });

    return (
      <ChartWidgetFrame>
        <div className="min-h-0 flex-1">
          <LineChartContent
            data={mergedData}
            series={series}
            parsedData={parsedData}
            widgetSettings={normalizedSettings}
          />
        </div>
      </ChartWidgetFrame>
    );
  }
}
